/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { ProductDimensionFull } from '@msdyn365-commerce/commerce-entities';
import { IImageSettings, Image } from '@msdyn365-commerce/core';
import { AttributeValue, ProductPrice, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { Button } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React from 'react';
import { filteredProductAttributeList, incExcPriceFormat, isRequestPrice, taxPercentage } from '../../../../utils/helper';

import { IAddLineToTemplateProps } from '.';
import { IOrderTemplateLineViewModel } from '../../krannich-c-order-template';

export interface IProductListProps extends IAddLineToTemplateProps {
    products: ProductSearchResult[];
    imageSettings: IImageSettings;
    searchForm: React.ReactNode;
    clickHandler(product: ProductSearchResult): Promise<ProductDimensionFull[]>;
    highlightSearchTerm(name: string): React.ReactNode;
    renderResultCountText(): string;
    productSelection: IOrderTemplateLineViewModel[];
    isAlreadyProductMessage?: boolean;
    productIndex?: number[];
}

export const ProductList = (props: IProductListProps) => {
    const {
        products,
        context: {
            actionContext: {
                requestContext: { apiSettings }
            },
            request: { gridSettings }
        },
        clickHandler,
        highlightSearchTerm,
        renderResultCountText,
        resources: { selectProductButtonText },
        imageSettings,
        customer,
        activePrices,
        enabledIncExPrices
    } = props;
    const getManufacturarFromAttribute = (attributeValues: AttributeValue[], attributeIdentifier: string) => {
        let ob;
        attributeValues.map((e: any, index: number) => {
            if (e?.identifier === attributeIdentifier) {
                ob = (
                    <div className='_manufacturer' key={index}>
                        <span className='_title'> {e?.Name}: </span>
                        <span className='_value'> {e?.TextValue}</span>
                    </div>
                );
            }
        });
        return ob;
    };
    return (
        <>
            <p className='msc-add-line-to-template__search-result-count' aria-live='assertive'>
                {renderResultCountText()}
            </p>
            {props.searchForm}
            <div className='msc-add-line-to-template__product-list'>
                {products.map((product: ProductSearchResult, index: number) => {
                    const { ItemId, Name, PrimaryImageUrl } = product;
                    const productPrice = activePrices.find(e => {
                        if (e.ItemId === ItemId) {
                            return e;
                        }
                        return null;
                    });
                    const customerTaxPercentage = taxPercentage(customer?.result!);
                    const priceJSX = incExcPriceFormat(
                        productPrice?.CustomerContextualPrice!,
                        props.context,
                        enabledIncExPrices,
                        customerTaxPercentage
                    );
                    const priceObject: ProductPrice = {
                        AdjustedPrice: productPrice?.AdjustedPrice,
                        DiscountAmount: productPrice?.DiscountAmount,
                        TradeAgreementPrice: productPrice?.TradeAgreementPrice,
                        SalesAgreementPrice: productPrice?.SalesAgreementPrice,
                        CustomerContextualPrice: productPrice?.CustomerContextualPrice,
                        ItemId
                    };
                    const fallBackImg = getFallbackImageUrl(ItemId, apiSettings, props.context.request.app?.config?.OmniChannelMedia) ?? '';
                    let isBundle: boolean | undefined = false;
                    product.AttributeValues?.map(e => {
                        if (e.Name === 'IsBundleProduct') {
                            if (e.BooleanValue === true) {
                                isBundle = true;
                            } else {
                                isBundle = false;
                            }
                        } else {
                            isBundle = false;
                        }
                    });
                    const productAttributes = filteredProductAttributeList(
                        product?.AttributeValues,
                        props.context.request.app.config.kcProductAttribute
                    );

                    return (
                        <div key={`product-${index}`} className='msc-add-line-to-template__product'>
                            <div className='msc-add-line-to-template__product__positioning-container-1'>
                                <Image
                                    altText={Name ?? 'image'}
                                    requestContext={props.context.actionContext.requestContext}
                                    src={PrimaryImageUrl ?? ''}
                                    fallBackSrc={fallBackImg}
                                    className='thumbnail'
                                    gridSettings={gridSettings!}
                                    imageSettings={imageSettings}
                                    loadFailureBehavior='empty'
                                />
                                <div className='msc-add-line-to-template__product__attributes' aria-live='polite'>
                                    <div className='msc-add-line-to-template__product__id'>
                                        <div className='item-number'>
                                            <span className='_title'> Item Number:</span>
                                            {ItemId}
                                        </div>
                                        {product &&
                                            product?.AttributeValues &&
                                            getManufacturarFromAttribute(productAttributes, 'Manufacturer')}
                                    </div>

                                    {highlightSearchTerm(Name ?? '')}
                                </div>
                            </div>
                            <div className='msc-add-line-to-template__product__price-container'>
                                {isRequestPrice(priceObject, customer?.result?.LineDiscountGroup!, isBundle) ? (
                                    <div className='no-price-wrapper'>
                                        <span className='label'>Request Price</span>
                                        <div className='tooltip-wrapper' tabIndex={0}>
                                            <span className='tooltip-icon' />
                                            <span className='tooltip-text tooltip-left'>
                                                {
                                                    'You cannot add this product to cart. Please contact your local Krannich Solar office or sales representative for current price and availability'
                                                }
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <span>{priceJSX}</span>
                                )}
                                <ProductSelectButton product={product} buttonText={selectProductButtonText} clickHandler={clickHandler} />
                            </div>
                            {props.productIndex![0] === index && props.isAlreadyProductMessage ? (
                                <p className='already-added-error-message'>{props.resources.addToTemplateDuplicateError}</p>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </>
    );
};

interface IProductSelectButton {
    product: ProductSearchResult;
    buttonText: string;
    clickHandler(product: ProductSearchResult): Promise<ProductDimensionFull[]>;
}

const ProductSelectButton = (props: IProductSelectButton) => {
    // const [isBusy, setBusy] = useState(false);
    const { product, buttonText, clickHandler } = props;
    const getClickHandler = React.useCallback(async () => {
        // setBusy(!isBusy);
        await clickHandler(product);
        // setBusy(!isBusy);
    }, [clickHandler, product]);

    return (
        <Button
            className={classnames('msc-add-line-to-template__product__select-button')}
            aria-label={`${product.Name!} ${buttonText}`} // Announce as: 'Nooks surfboard' select product button
            onClick={getClickHandler}
        >
            {buttonText}
        </Button>
    );
};
